import Papa from 'papaparse';

export const submitFeedback = async (comment, sessionDetails) => {
  // Debug logs
  console.log('Submit Feedback - Session Details:', sessionDetails);
  
  // Format bundle details in a readable way
  const formatBundleDetails = (details) => {
    // Check if we have any valid component
    const hasAnyComponent = 
      details?.selectedScreen?.name || 
      details?.selectedTransducer?.name || 
      details?.selectedBattery?.name || 
      details?.selectedShuttle?.name || 
      details?.selectedPole?.name;
    
    if (!hasAnyComponent) {
      console.log('No components found in session details');
      return '\n\nNo bundle configured yet';
    }

    let bundleText = '\n\nbundleDetails\n\n';
    
    // Add each component if it exists
    if (details?.selectedScreen?.name) {
      bundleText += `Screen: ${details.selectedScreen.name}\n`;
    }
    
    if (details?.selectedTransducer?.name) {
      bundleText += `Transducer: ${details.selectedTransducer.name}\n`;
    }
    
    if (details?.selectedBattery?.name) {
      bundleText += `Battery: ${details.selectedBattery.name}\n`;
    }
    
    if (details?.selectedShuttle?.name) {
      bundleText += `Shuttle: ${details.selectedShuttle.name}\n`;
    }
    
    if (details?.selectedPole?.name) {
      bundleText += `Pole: ${details.selectedPole.name}\n`;
    }
    
    // Add total MSRP if available
    if (details?.totalMSRP) {
      bundleText += `Total MSRP: $${details.totalMSRP.toFixed(2)}`;
    }

    console.log('Formatted bundle text:', bundleText);
    return bundleText;
  };

  const formattedMessage = comment + formatBundleDetails(sessionDetails);
  console.log('Sending formatted message:', formattedMessage);

  const response = await fetch('https://formspree.io/f/mgvaevdy', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      message: formattedMessage
    })
  });

  if (!response.ok) {
    throw new Error('Failed to submit feedback');
  }

  return response;
};

// Helper function to get the API base URL
const getApiBaseUrl = () => {
  // Use window.REACT_APP_API_URL if available, otherwise default to the relative path
  // which works with the proxy configuration
  if (window.REACT_APP_API_URL) {
    return window.REACT_APP_API_URL;
  }
  
  // If we're running locally, use the proxy-friendly path
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return '';
  }
  
  // Default (production) - should be configured via environment variables in a real deployment
  return '';
};

// Direct function to fetch from Google Sheets CSV
export const fetchProductPricesFromCSV = async (productId = null, csvLookupId = null, exactMatchOnly = false) => {
  const csvUrl = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vTEy5_PFrbo0uwXTwhZqMvCAnO2GDob7JK7e2kemhP97RDw9X3TLAJEUINE0KDoGshCB9i2LV_CBIwv/pub?gid=1450571949&single=true&output=csv';
  console.log('Fetching price data from Google Sheet CSV for product:', productId);
  if (csvLookupId) {
    console.log(`Will use exact CSV lookup ID for matching: "${csvLookupId}"${exactMatchOnly ? ' (EXACT MATCH ONLY)' : ''}`);
  }
  
  try {
    // Determine if we're in a production build
    const isProduction = process.env.NODE_ENV === 'production' || 
                        !window.location.hostname.includes('localhost');
    
    console.log(`Environment detection: isProduction=${isProduction}, hostname=${window.location.hostname}, NODE_ENV=${process.env.NODE_ENV || 'undefined'}`);
                        
    // Use a CORS proxy in production to avoid CORS issues - try multiple options
    let fetchUrl;
    
    const timestamp = Date.now();
    
    if (isProduction) {
      // Try multiple CORS proxy options
      // Option 1: allorigins.win
      const corsProxies = [
        {
          name: 'allorigins',
          url: `https://api.allorigins.win/raw?url=${encodeURIComponent(`${csvUrl}${csvUrl.includes('?') ? '&' : '?'}_t=${timestamp}`)}`,
          headers: {} // No custom headers for this proxy
        },
        {
          name: 'cors-anywhere',
          url: `https://cors-anywhere.herokuapp.com/${csvUrl}${csvUrl.includes('?') ? '&' : '?'}_t=${timestamp}`,
          headers: {} // No custom headers for this proxy
        },
        {
          name: 'direct-with-no-cors',
          url: `${csvUrl}${csvUrl.includes('?') ? '&' : '?'}_t=${timestamp}`,
          headers: {},
          mode: 'no-cors'
        }
      ];
      
      // Try each CORS proxy option
      let lastError = null;
      
      for (const proxy of corsProxies) {
        try {
          console.log(`Trying ${proxy.name} CORS proxy: ${proxy.url}`);
          
          const response = await fetch(proxy.url, {
            method: 'GET',
            headers: proxy.headers,
            mode: proxy.mode || 'cors',
            // Removed problematic headers:
            // 'Cache-Control', 'Pragma', 'Expires'
          });
          
          if (response.ok || proxy.mode === 'no-cors') {
            fetchUrl = proxy.url;
            console.log(`Successfully fetched using ${proxy.name} proxy`);
            
            // If using no-cors mode, we need to return a default response since we can't read the content
            if (proxy.mode === 'no-cors') {
              console.log('Using no-cors mode - returning fallback data');
              return {
                success: true,
                data: getFallbackData(productId),
                source: 'fallback-data',
                usingFallback: true,
                timestamp: timestamp
              };
            }
            
            const csvText = await response.text();
            // Process the successful response
            if (csvText && csvText.includes(',') && csvText.includes('\n')) {
              console.log('CSV data received, size:', csvText.length, 'bytes');
              console.log('CSV data first 100 chars:', csvText.substring(0, 100).replace(/\n/g, '\\n'));
              
              // Parse and process the CSV data
              const parsedData = parseCSVWithPapa(csvText);
              console.log('Parsed CSV data rows:', parsedData.length);
              
              if (parsedData.length === 0) {
                console.error('CSV parsing produced no rows. Raw data excerpt:', csvText.substring(0, 200));
                throw new Error('CSV parsing produced no data rows');
              }
              
              // Filter by product ID if provided
              let filteredData = parsedData;
              if (productId) {
                // Use enhanced matching algorithm
                const normalizedProductId = productId.toLowerCase().replace(/-/g, ' ');
                
                // Helper function for fuzzy matching (old approach)
                function getFuzzyMatches(data, normalizedProductId) {
                  return data.filter(item => {
                    if (!item.Product) return false;
                    
                    const normalizedItemProduct = item.Product.toLowerCase().replace(/-/g, ' ');
                    
                    // Check for exact match
                    if (normalizedItemProduct === normalizedProductId) return true;
                    
                    // Check if item product contains the product ID
                    if (normalizedItemProduct.includes(normalizedProductId)) return true;
                    
                    // Check if product ID contains the item product
                    if (normalizedProductId.includes(normalizedItemProduct)) return true;
                    
                    // Check for word matches (any word in product ID appears in item product)
                    const productIdWords = normalizedProductId.split(' ');
                    for (const word of productIdWords) {
                      if (word.length > 3 && normalizedItemProduct.includes(word)) return true;
                    }
                    
                    return false;
                  });
                }
                
                // After parsing the CSV, prioritize exact matching with csvLookupId if provided
                if (csvLookupId && parsedData && parsedData.length > 0) {
                  console.log(`Attempting exact match using csvLookupId: "${csvLookupId}"`);
                  
                  // Strict exact matching with csvLookupId
                  const exactIdMatches = parsedData.filter(item => {
                    return item.Product === csvLookupId;
                  });
                  
                  if (exactIdMatches.length > 0) {
                    console.log(`Found ${exactIdMatches.length} exact matches for csvLookupId "${csvLookupId}"`);
                    filteredData = exactIdMatches;
                    
                    // Log matches for debugging
                    if (filteredData.length > 0) {
                      console.log('Exact matches found:');
                      filteredData.slice(0, 3).forEach(item => {
                        console.log(`- "${item.Product}" (${item.Price})`);
                      });
                    }
                    
                    // Continue with data processing - the exact matches will be used
                  } else {
                    console.log(`No exact matches found for csvLookupId "${csvLookupId}"`);
                    
                    // If exactMatchOnly is true, return empty results rather than falling back to fuzzy matching
                    if (exactMatchOnly) {
                      console.log(`EXACT MATCH ONLY MODE: No exact matches found for "${csvLookupId}". Returning empty results.`);
                      return {
                        success: true,
                        data: [],
                        source: 'google-sheets-csv-exact-match-only',
                        timestamp: timestamp,
                        rowCount: 0
                      };
                    }
                    // Otherwise continue with standard matching
                  }
                }
                
                // Only proceed with standard product ID matching if:
                // 1. We don't have csvLookupId, OR
                // 2. We didn't find matches with csvLookupId AND exactMatchOnly is false
                if ((!csvLookupId || filteredData.length === 0) && !exactMatchOnly) {
                  // Convert product ID format to expected display format
                  const formattedProductName = productId
                    .split('-')
                    .map(word => {
                      // Special case for ECHOMAP - always all caps
                      if (word.toLowerCase() === 'echomap') return 'ECHOMAP';
                      
                      // Special case for GPSMAP - always all caps
                      if (word.toLowerCase() === 'gpsmap') return 'GPSMAP';
                      
                      // Special case for UHD/UHD2 - always all caps
                      if (word.toLowerCase() === 'uhd') return 'UHD';
                      if (word.toLowerCase() === 'uhd2') return 'UHD2';
                      
                      // Special case for LVS (LiveScope) - always all caps
                      if (word.toLowerCase() === 'lvs') return 'LVS';
                      
                      // Special case for Plus - capitalize first letter
                      if (word.toLowerCase() === 'plus') return 'Plus';
                      
                      // Default capitalization for other words
                      return word.charAt(0).toUpperCase() + word.slice(1);
                    })
                    .join(' ');
                  
                  console.log(`Attempting exact match for product: "${formattedProductName}"`);
                  
                  // First try exact match by formatted name
                  let exactMatches = parsedData.filter(item => {
                    return item.Product === formattedProductName;
                  });
                  
                  // If we found exact matches, use those
                  if (exactMatches.length > 0) {
                    console.log(`Found ${exactMatches.length} exact matches for "${formattedProductName}"`);
                    filteredData = exactMatches;
                    
                    // Log matches for debugging
                    if (filteredData.length > 0) {
                      console.log('Matches found:');
                      filteredData.slice(0, 3).forEach(item => {
                        console.log(`- "${item.Product}" (${item.Price})`);
                      });
                    }
                  } 
                  // Special handling for ECHOMAP, GPSMAP, and LiveScope products
                  else if (productId.toLowerCase().includes('echomap') || 
                           productId.toLowerCase().includes('gpsmap') ||
                           productId.toLowerCase().includes('livescope')) {
                    
                    // Try to determine the exact product name based on known patterns
                    let exactProductName = null;
                    
                    // ECHOMAP UHD2 73sv
                    if (normalizedProductId.includes('echomap') && normalizedProductId.includes('73sv')) {
                      exactProductName = 'ECHOMAP UHD2 73sv';
                    } 
                    // ECHOMAP UHD2 93sv
                    else if (normalizedProductId.includes('echomap') && normalizedProductId.includes('93sv')) {
                      exactProductName = 'ECHOMAP UHD2 93sv';
                    }
                    // GPSMAP 8610xsv
                    else if (normalizedProductId.includes('gpsmap') && normalizedProductId.includes('8610xsv')) {
                      exactProductName = 'GPSMAP 8610xsv';
                    }
                    // GPSMAP 8612xsv
                    else if (normalizedProductId.includes('gpsmap') && normalizedProductId.includes('8612xsv')) {
                      exactProductName = 'GPSMAP 8612xsv';
                    }
                    // GPSMAP 943xsv
                    else if (normalizedProductId.includes('gpsmap') && normalizedProductId.includes('943xsv')) {
                      exactProductName = 'GPSMAP 943xsv';
                    }
                    // GPSMAP 1243xsv
                    else if (normalizedProductId.includes('gpsmap') && normalizedProductId.includes('1243xsv')) {
                      exactProductName = 'GPSMAP 1243xsv';
                    }
                    // GPSMAP 1042xsv
                    else if (normalizedProductId.includes('gpsmap') && normalizedProductId.includes('1042xsv')) {
                      exactProductName = 'GPSMAP 1042xsv';
                    }
                    // GPSMAP 1242xsv
                    else if (normalizedProductId.includes('gpsmap') && normalizedProductId.includes('1242xsv')) {
                      exactProductName = 'GPSMAP 1242xsv';
                    }
                    // LiveScope Plus LVS34
                    else if (normalizedProductId.includes('livescope') && normalizedProductId.includes('plus')) {
                      exactProductName = 'LiveScope Plus LVS34';
                    }
                    
                    if (exactProductName) {
                      console.log(`Using known exact name mapping: "${exactProductName}"`);
                      
                      filteredData = parsedData.filter(item => {
                        return item.Product === exactProductName;
                      });
                      
                      console.log(`Found ${filteredData.length} exact matches for "${exactProductName}"`);
                    } else {
                      // Fallback to standard matching if exact match failed
                      console.log(`No exact matches found for "${formattedProductName}", falling back to fuzzy matching`);
                      filteredData = getFuzzyMatches(parsedData, normalizedProductId);
                    }
                  } else {
                    // Fallback to standard matching if exact match failed
                    console.log(`No exact matches found for "${formattedProductName}", falling back to fuzzy matching`);
                    filteredData = getFuzzyMatches(parsedData, normalizedProductId);
                  }
                }
                
                console.log(`Filtered to ${filteredData.length} rows for product ID: ${productId}`);
              }
              
              // Process data to match expected format
              const processedData = filteredData.map(item => {
                // Validate price format
                let numericPrice = 0;
                try {
                  numericPrice = parseFloat((item.Price || '').replace(/[$,]/g, '') || '0');
                  if (isNaN(numericPrice)) {
                    console.warn(`Invalid price format: "${item.Price}" for product "${item.Product}"`);
                    numericPrice = 0;
                  }
                } catch (error) {
                  console.error(`Error parsing price "${item.Price}":`, error);
                  numericPrice = 0;
                }
                
                return {
                  Price: item.Price || 'N/A',
                  Product: item.Product || 'Unknown Product',
                  'Date Time Stamp': (item.DateTimeStamp && item.DateTimeStamp.trim()) ? 
                    item.DateTimeStamp.trim() : 
                    new Date().toISOString(),
                  Source: item.Website || 'Unknown',
                  'Job Link': item.AffiliateLink || '',
                  numeric_price: numericPrice
                };
              });
              
              console.log('Processed data from CSV:', processedData.length, 'items');
              if (processedData.length > 0) {
                console.log('First item sample:', JSON.stringify(processedData[0]));
              }
              
              return {
                success: true,
                data: processedData,
                source: 'google-sheets-csv',
                timestamp: timestamp,
                rowCount: processedData.length
              };
            } else {
              throw new Error('Invalid CSV format received');
            }
          } else {
            lastError = new Error(`Failed with status: ${response.status}`);
            console.warn(`${proxy.name} proxy failed with status ${response.status}`);
          }
        } catch (proxyError) {
          lastError = proxyError;
          console.warn(`Error using ${proxy.name} proxy:`, proxyError.message);
          // Continue to the next proxy
        }
      }
      
      // If we get here, all proxies failed
      throw lastError || new Error('All CORS proxies failed');
    } else {
      // In development, use direct URL with cache busting
      fetchUrl = `${csvUrl}${csvUrl.includes('?') ? '&' : '?'}_t=${timestamp}`;
      console.log('Direct fetch in development:', fetchUrl);
      
      const response = await fetch(fetchUrl, {
        method: 'GET'
        // Removed problematic headers
      });
      
      if (!response.ok) {
        console.error('CSV fetch failed with status:', response.status);
        const responseText = await response.text();
        console.error('Error response text (truncated):', responseText.substring(0, 200));
        throw new Error(`Failed to fetch CSV: ${response.status}`);
      }
      
      const csvText = await response.text();
      console.log('CSV data received, size:', csvText.length, 'bytes');
      console.log('CSV data first 100 chars:', csvText.substring(0, 100).replace(/\n/g, '\\n'));
      
      // Validate CSV format before parsing
      if (csvText.length < 50 || !csvText.includes(',') || !csvText.includes('\n')) {
        console.error('CSV data appears invalid. Sample:', csvText.substring(0, 200));
        throw new Error('CSV data format appears invalid');
      }
      
      // Parse CSV using Papa Parse
      const parsedData = parseCSVWithPapa(csvText);
      console.log('Parsed CSV data rows:', parsedData.length);
      
      if (parsedData.length === 0) {
        console.error('CSV parsing produced no rows. Raw data excerpt:', csvText.substring(0, 200));
        throw new Error('CSV parsing produced no data rows');
      }
      
      // Filter by product ID if provided
      let filteredData = parsedData;
      if (productId) {
        // Use enhanced matching algorithm
        const normalizedProductId = productId.toLowerCase().replace(/-/g, ' ');
        
        // Helper function for fuzzy matching (old approach)
        function getFuzzyMatches(data, normalizedProductId) {
          return data.filter(item => {
            if (!item.Product) return false;
            
            const normalizedItemProduct = item.Product.toLowerCase().replace(/-/g, ' ');
            
            // Check for exact match
            if (normalizedItemProduct === normalizedProductId) return true;
            
            // Check if item product contains the product ID
            if (normalizedItemProduct.includes(normalizedProductId)) return true;
            
            // Check if product ID contains the item product
            if (normalizedProductId.includes(normalizedItemProduct)) return true;
            
            // Check for word matches (any word in product ID appears in item product)
            const productIdWords = normalizedProductId.split(' ');
            for (const word of productIdWords) {
              if (word.length > 3 && normalizedItemProduct.includes(word)) return true;
            }
            
            return false;
          });
        }
        
        // After parsing the CSV, prioritize exact matching with csvLookupId if provided
        if (csvLookupId && parsedData && parsedData.length > 0) {
          console.log(`Attempting exact match using csvLookupId: "${csvLookupId}"`);
          
          // Strict exact matching with csvLookupId
          const exactIdMatches = parsedData.filter(item => {
            return item.Product === csvLookupId;
          });
          
          if (exactIdMatches.length > 0) {
            console.log(`Found ${exactIdMatches.length} exact matches for csvLookupId "${csvLookupId}"`);
            filteredData = exactIdMatches;
            
            // Log matches for debugging
            if (filteredData.length > 0) {
              console.log('Exact matches found:');
              filteredData.slice(0, 3).forEach(item => {
                console.log(`- "${item.Product}" (${item.Price})`);
              });
            }
            
            // Continue with data processing - the exact matches will be used
          } else {
            console.log(`No exact matches found for csvLookupId "${csvLookupId}"`);
            
            // If exactMatchOnly is true, return empty results rather than falling back to fuzzy matching
            if (exactMatchOnly) {
              console.log(`EXACT MATCH ONLY MODE: No exact matches found for "${csvLookupId}". Returning empty results.`);
              return {
                success: true,
                data: [],
                source: 'google-sheets-csv-exact-match-only',
                timestamp: timestamp,
                rowCount: 0
              };
            }
            // Otherwise continue with standard matching
          }
        }
        
        // Only proceed with standard product ID matching if:
        // 1. We don't have csvLookupId, OR
        // 2. We didn't find matches with csvLookupId AND exactMatchOnly is false
        if ((!csvLookupId || filteredData.length === 0) && !exactMatchOnly) {
          // Convert product ID format to expected display format
          const formattedProductName = productId
            .split('-')
            .map(word => {
              // Special case for ECHOMAP - always all caps
              if (word.toLowerCase() === 'echomap') return 'ECHOMAP';
              
              // Special case for GPSMAP - always all caps
              if (word.toLowerCase() === 'gpsmap') return 'GPSMAP';
              
              // Special case for UHD/UHD2 - always all caps
              if (word.toLowerCase() === 'uhd') return 'UHD';
              if (word.toLowerCase() === 'uhd2') return 'UHD2';
              
              // Special case for LVS (LiveScope) - always all caps
              if (word.toLowerCase() === 'lvs') return 'LVS';
              
              // Special case for Plus - capitalize first letter
              if (word.toLowerCase() === 'plus') return 'Plus';
              
              // Default capitalization for other words
              return word.charAt(0).toUpperCase() + word.slice(1);
            })
            .join(' ');
          
          console.log(`Attempting exact match for product: "${formattedProductName}"`);
          
          // First try exact match by formatted name
          let exactMatches = parsedData.filter(item => {
            return item.Product === formattedProductName;
          });
          
          // If we found exact matches, use those
          if (exactMatches.length > 0) {
            console.log(`Found ${exactMatches.length} exact matches for "${formattedProductName}"`);
            filteredData = exactMatches;
            
            // Log matches for debugging
            if (filteredData.length > 0) {
              console.log('Matches found:');
              filteredData.slice(0, 3).forEach(item => {
                console.log(`- "${item.Product}" (${item.Price})`);
              });
            }
          } 
          // Special handling for ECHOMAP, GPSMAP, and LiveScope products
          else if (productId.toLowerCase().includes('echomap') || 
                   productId.toLowerCase().includes('gpsmap') ||
                   productId.toLowerCase().includes('livescope')) {
            
            // Try to determine the exact product name based on known patterns
            let exactProductName = null;
            
            // ECHOMAP UHD2 73sv
            if (normalizedProductId.includes('echomap') && normalizedProductId.includes('73sv')) {
              exactProductName = 'ECHOMAP UHD2 73sv';
            } 
            // ECHOMAP UHD2 93sv
            else if (normalizedProductId.includes('echomap') && normalizedProductId.includes('93sv')) {
              exactProductName = 'ECHOMAP UHD2 93sv';
            }
            // GPSMAP 8610xsv
            else if (normalizedProductId.includes('gpsmap') && normalizedProductId.includes('8610xsv')) {
              exactProductName = 'GPSMAP 8610xsv';
            }
            // GPSMAP 8612xsv
            else if (normalizedProductId.includes('gpsmap') && normalizedProductId.includes('8612xsv')) {
              exactProductName = 'GPSMAP 8612xsv';
            }
            // GPSMAP 943xsv
            else if (normalizedProductId.includes('gpsmap') && normalizedProductId.includes('943xsv')) {
              exactProductName = 'GPSMAP 943xsv';
            }
            // GPSMAP 1243xsv
            else if (normalizedProductId.includes('gpsmap') && normalizedProductId.includes('1243xsv')) {
              exactProductName = 'GPSMAP 1243xsv';
            }
            // GPSMAP 1042xsv
            else if (normalizedProductId.includes('gpsmap') && normalizedProductId.includes('1042xsv')) {
              exactProductName = 'GPSMAP 1042xsv';
            }
            // GPSMAP 1242xsv
            else if (normalizedProductId.includes('gpsmap') && normalizedProductId.includes('1242xsv')) {
              exactProductName = 'GPSMAP 1242xsv';
            }
            // LiveScope Plus LVS34
            else if (normalizedProductId.includes('livescope') && normalizedProductId.includes('plus')) {
              exactProductName = 'LiveScope Plus LVS34';
            }
            
            if (exactProductName) {
              console.log(`Using known exact name mapping: "${exactProductName}"`);
              
              filteredData = parsedData.filter(item => {
                return item.Product === exactProductName;
              });
              
              console.log(`Found ${filteredData.length} exact matches for "${exactProductName}"`);
            } else {
              // Fallback to standard matching if exact match failed
              console.log(`No exact matches found for "${formattedProductName}", falling back to fuzzy matching`);
              filteredData = getFuzzyMatches(parsedData, normalizedProductId);
            }
          } else {
            // Fallback to standard matching if exact match failed
            console.log(`No exact matches found for "${formattedProductName}", falling back to fuzzy matching`);
            filteredData = getFuzzyMatches(parsedData, normalizedProductId);
          }
        }
        
        console.log(`Filtered to ${filteredData.length} rows for product ID: ${productId}`);
      }
      
      // Process data to match expected format
      const processedData = filteredData.map(item => {
        // Validate price format
        let numericPrice = 0;
        try {
          numericPrice = parseFloat((item.Price || '').replace(/[$,]/g, '') || '0');
          if (isNaN(numericPrice)) {
            console.warn(`Invalid price format: "${item.Price}" for product "${item.Product}"`);
            numericPrice = 0;
          }
        } catch (error) {
          console.error(`Error parsing price "${item.Price}":`, error);
          numericPrice = 0;
        }
        
        return {
          Price: item.Price || 'N/A',
          Product: item.Product || 'Unknown Product',
          'Date Time Stamp': (item.DateTimeStamp && item.DateTimeStamp.trim()) ? 
            item.DateTimeStamp.trim() : 
            new Date().toISOString(),
          Source: item.Website || 'Unknown',
          'Job Link': item.AffiliateLink || '',
          numeric_price: numericPrice
        };
      });
      
      console.log('Processed data from CSV:', processedData.length, 'items');
      if (processedData.length > 0) {
        console.log('First item sample:', JSON.stringify(processedData[0]));
      }
      
      return {
        success: true,
        data: processedData,
        source: 'google-sheets-csv',
        timestamp: timestamp,
        rowCount: processedData.length
      };
    }
  } catch (error) {
    console.error('Error fetching/parsing CSV:', error.message, error.stack);
    // Throw with more detailed info
    throw new Error(`CSV fetch error: ${error.message} (Check console for details)`);
  }
};

// Helper function to get fallback data
function getFallbackData(productId) {
  // Generate a properly formatted date string that will be visible in the UI
  const timestamp = new Date().toISOString();
  
  if (productId && productId.toLowerCase().includes('livescope')) {
    return [
      {
        'Price': '$1,699.99',
        'Product': 'LiveScope Plus LVS34',
        'Date Time Stamp': timestamp,
        'Source': 'Amazon',
        'Job Link': 'https://www.amazon.com/Garmin-010-02706-00-LiveScope-Transducer-Freshwater/dp/B09SGT9T88',
        'numeric_price': 1699.99
      },
      {
        'Price': '$1,329.99',
        'Product': 'LiveScope Plus LVS34',
        'Date Time Stamp': timestamp,
        'Source': 'Scheels',
        'Job Link': 'https://www.scheels.com/p/75375929845',
        'numeric_price': 1329.99
      }
    ];
  }
  
  // Default fallback data
  return [
    {
      'Price': '$1,699.99',
      'Product': 'Garmin Product',
      'Date Time Stamp': timestamp,
      'Source': 'Amazon',
      'Job Link': 'https://www.amazon.com/',
      'numeric_price': 1699.99
    }
  ];
}

// Parse CSV using Papa Parse
function parseCSVWithPapa(csvText) {
  // Check if Papa Parse is available
  if (typeof Papa === 'undefined') {
    console.error('Papa Parse is not available, falling back to manual CSV parsing');
    return parseCSV(csvText);
  }
  
  try {
    const results = Papa.parse(csvText, {
      header: true,
      skipEmptyLines: true,
      transform: (value) => value.trim(),
      transformHeader: (header) => header.trim(),
      error: (error) => {
        console.error('Papa Parse error:', error.message);
      }
    });
    
    // Log parsing stats
    console.log('Papa Parse results:', {
      rowCount: results.data.length,
      errors: results.errors && results.errors.length,
      fields: results.meta && results.meta.fields ? results.meta.fields.join(', ') : 'none'
    });
    
    // Check for parsing errors
    if (results.errors && results.errors.length > 0) {
      console.warn('CSV parsing had errors:', results.errors);
      results.errors.forEach((error, index) => {
        console.warn(`Parse error ${index + 1}:`, error.message, 
                    error.row !== undefined ? `at row ${error.row}` : '');
      });
    }
    
    return results.data;
  } catch (error) {
    console.error('Error during Papa Parse processing:', error);
    // Fall back to the manual parser
    console.warn('Falling back to manual CSV parsing');
    return parseCSV(csvText);
  }
}

// Original CSV parser function (kept as fallback)
function parseCSV(text) {
  try {
    const lines = text.split('\n');
    if (lines.length < 2) {
      console.error('CSV has too few lines:', lines.length);
      return [];
    }
    
    const headers = parseCSVRow(lines[0]);
    console.log('CSV headers:', headers);
    
    if (headers.length < 2) {
      console.error('CSV has too few columns:', headers);
      return [];
    }
    
    return lines.slice(1)
      .filter(line => line.trim())
      .map((line, index) => {
        try {
          const values = parseCSVRow(line);
          
          // Create an object from headers and values
          const item = {};
          headers.forEach((header, i) => {
            item[header.trim()] = i < values.length ? values[i].trim() : '';
          });
          
          return item;
        } catch (error) {
          console.error(`Error parsing CSV row ${index + 1}:`, error, 'Row:', line);
          return null;
        }
      })
      .filter(item => item !== null); // Remove rows that failed to parse
  } catch (error) {
    console.error('Fatal error in CSV parsing:', error);
    return [];
  }
}

// Helper function to parse CSV row properly handling quotes
function parseCSVRow(row) {
  const values = [];
  let inQuotes = false;
  let currentValue = '';
  
  for (let i = 0; i < row.length; i++) {
    const char = row[i];
    
    if (char === '"') {
      // Handle escaped quotes (double quotes inside quoted strings)
      if (inQuotes && i + 1 < row.length && row[i + 1] === '"') {
        currentValue += '"';
        i++; // Skip the next quote
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      values.push(currentValue);
      currentValue = '';
    } else {
      currentValue += char;
    }
  }
  
  // Add the last value
  values.push(currentValue);
  
  // Clean up values (remove surrounding quotes)
  return values.map(value => value.replace(/^"|"$/g, '').trim());
}

// Updated function to fetch product price data - uses direct CSV in production
export const fetchProductPrices = async (productId = null, retryCount = 0, maxRetries = 20, csvLookupId = null, exactMatchOnly = false) => {
  const timestamp = Date.now();
  console.log(`fetchProductPrices for ${productId} (retry ${retryCount}/${maxRetries})`);
  if (csvLookupId) {
    console.log(`Using exact CSV lookup ID: "${csvLookupId}"${exactMatchOnly ? ' (EXACT MATCH ONLY MODE)' : ''}`);
  }
  
  // Try with the server first - if that fails, fall back to direct CSV fetch
  try {
    console.log(`Fetching price data for product: ${productId} (Attempt ${retryCount + 1}/${maxRetries + 1})`);
    
    // Always use direct CSV fetching in production or when deployed
    // Only use server API in development when server is available
    const isServerAvailable = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    if (!isServerAvailable) {
      console.log('Using direct CSV fetch method (production mode)');
      return await fetchProductPricesFromCSV(productId, csvLookupId, exactMatchOnly);
    }
    
    // Development mode - try to use the server API first
    console.log('Using server API (development mode)');
    
    // Determine the base URL based on the environment
    // Try multiple ports in case one is not working
    const possiblePorts = [5002, 5004, 3001, 3000, 5000];
    const basePort = possiblePorts[retryCount % possiblePorts.length]; // Cycle through ports based on retry count
    const baseUrl = `http://${window.location.hostname}:${basePort}`;
      
    // Log the API URL being used
    const apiUrl = `${baseUrl}/api/prices${productId ? `?productId=${encodeURIComponent(productId)}` : ''}`;
    console.log(`Attempt ${retryCount + 1}: Fetching prices from API URL:`, apiUrl);
    
    // Add cache-busting parameter
    const cacheBustUrl = `${apiUrl}${apiUrl.includes('?') ? '&' : '?'}_t=${timestamp}`;
    
    // Try server API with a short timeout
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 4000); // 4 second timeout (increased from 2.5s)
      
      const response = await fetch(cacheBustUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        credentials: 'same-origin',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('API response:', result);
      
      if (!result.success) {
        throw new Error(result.error || 'Unknown error from API');
      }
      
      // Process the data if needed
      if (result.data && result.data.length > 0) {
        // Ensure numeric_price is set properly
        result.data = result.data.map(item => ({
          ...item,
          numeric_price: item.numeric_price || parseFloat((item.Price || '').replace(/[$,]/g, '') || '0')
        }));
        
        return {
          success: true,
          data: result.data || [],
          source: result.source || 'api',
          timestamp: result.timestamp || timestamp
        };
      } else {
        // If we got success but no data, that's an error condition
        throw new Error('API returned success but no price data');
      }
    } catch (apiError) {
      console.warn(`Server API failed (port ${basePort}), trying direct CSV:`, apiError.message);
      
      try {
        return await fetchProductPricesFromCSV(productId, csvLookupId, exactMatchOnly);
      } catch (csvError) {
        // If both API and CSV methods fail and we haven't exhausted retries, try again
        console.warn(`CSV method also failed: ${csvError.message}`);
        
        // Always retry with a shorter delay as we go - users need prices
        const retryDelay = Math.max(500, 2000 - (retryCount * 100)); // Decrease delay with each retry, min 500ms
        
        console.warn(`Fetch attempt ${retryCount + 1} failed. Retrying in ${retryDelay}ms...`);
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        
        // Always retry, never give up until we get some data
        return fetchProductPrices(productId, retryCount + 1, maxRetries, csvLookupId, exactMatchOnly);
      }
    }
  } catch (error) {
    console.error(`Error fetching price data (attempt ${retryCount + 1}/${maxRetries + 1}):`, error);
    
    // Always retry with a shorter delay as we go
    const retryDelay = Math.max(800, 3000 - (retryCount * 150)); // Decrease delay with each retry, min 800ms
    
    console.warn(`Fetch attempt ${retryCount + 1} failed. Retrying in ${retryDelay}ms...`);
    
    // Wait before retrying
    await new Promise(resolve => setTimeout(resolve, retryDelay));
    
    // Always keep retrying
    return {
      success: false,
      error: error.message,
      data: [], // Return empty data array instead of fallback
      source: 'error-fallback',
      timestamp: Date.now()
    };
  }
}; 